// two indicators only use three colours
const calculateColourValuesThree = (data, max) => {
  const result = {
    colour: 'GREY',
    value: 0.0,
    max,
  };

  if (!data) return result;

  const colours = {
    green: 'green',
    yellow: 'yellow', // was not mapped in powerbi report
    orange: 'yellow',
    red: 'orange',
    grey: 'grey',
  };

  Object.keys(colours).forEach((col) => {
    if (data[col] !== null && data[col] > 0.0) {
      result.colour = colours[col].toLocaleUpperCase();
      result.value = data[col];
    }
  });

  if (data.source) {
    result.source = data.source;
  }

  return result;
};

const calculateColourValues = (data, max) => {
  const result = {
    colour: 'GREY',
    value: 0.0,
    max,
  };

  const colours = [
    'green',
    'yellow',
    'orange',
    'red',
    'grey',
  ];

  colours.forEach((col) => {
    if (data[col] !== null && data[col] > 0.0) {
      result.colour = col.toLocaleUpperCase();
      result.value = data[col];
    }
  });

  if (data.source) {
    result.source = data.source;
  }

  return result;
};

const findMax = (data, field) => {
  const values = data.map((row) => row[field]);
  return Math.max(...values);
};

const convertAreaLineGraph = (data, tafield, pgfield, divideBy, multiplyBy) => {
  const allYears = data.map((a) => a.Year);
  const distinct_years = [...new Set(allYears)].sort();
  const values = [];
  let adjustYmax = null;

  if (divideBy) {
    // lets find the max and see if it is more than divideBy
    const taMax = findMax(data, tafield);
    const pgMax = findMax(data, pgfield);

    const lowMax = findMax(data, 'LowPercentile');
    const highMax = findMax(data, 'HighPercentile');

    const max = Math.max(Math.max(taMax, pgMax), Math.max(lowMax, highMax));

    if (max > 0 && max < divideBy) {
      adjustYmax = max / divideBy;
    }
  }

  for (let j = 0; j < 4; j++) {
    values.push([]);
  }

  distinct_years.forEach((year) => {
    const row = data.find((d) => d.Year === year);
    if (divideBy) {
      // TA - fund value
      values[0].push(divideBy > 0 ? row[tafield] / divideBy : row[tafield]);
      // peer group
      values[1].push(divideBy > 0 ? row[pgfield] / divideBy : row[pgfield]);

      // grey area
      // low percentile
      values[2].push(divideBy > 0 ? row.LowPercentile / divideBy : row.LowPercentile);
      // high percentile
      values[3].push(divideBy > 0 ? row.HighPercentile / divideBy : row.HighPercentile);
    } else if (multiplyBy) {
      // TA - fund value
      values[0].push(multiplyBy > 0 ? row[tafield] * multiplyBy : row[tafield]);
      // peer group
      values[1].push(multiplyBy > 0 ? row[pgfield] * multiplyBy : row[pgfield]);

      // grey area
      // low percentile
      values[2].push(multiplyBy > 0 ? row.LowPercentile * multiplyBy : row.LowPercentile);
      // high percentile
      values[3].push(multiplyBy > 0 ? row.HighPercentile * multiplyBy : row.HighPercentile);
    } else {
      // TA - fund value
      values[0].push(row[tafield]);
      // peer group
      values[1].push(row[pgfield]);

      // grey area
      // low percentile
      values[2].push(row.LowPercentile);
      // high percentile
      values[3].push(row.HighPercentile);
    }
  });

  return {
    // years
    years: distinct_years,
    // data
    values,
    adjustYmax,
  };
};

const convertLineGraph = (data, fields, divideBy, multiplyBy, zeroFilter) => {
  const allYears = data.map((a) => a.Year);
  const distinct_years = [...new Set(allYears)].sort();
  const values = [];

  for (let j = 0; j < fields.length; j++) {
    values.push([]);
  }

  distinct_years.forEach((year) => {
    const row = data.find((d) => d.Year === year);

    fields.forEach((field, index) => {
      if (row[field] !== null) {
        if (zeroFilter && parseInt(row[field], 10) === 0) {
          values[index].push(null);
        } else if (divideBy) {
          values[index].push(row[field] / divideBy);
        } else if (multiplyBy) {
          values[index].push(row[field] * multiplyBy);
        } else {
          values[index].push(row[field]);
        }
      } else {
        values[index].push(null);
      }
    });
  });

  return {
    // years
    years: distinct_years,
    // data
    values,
  };
};

// loop over the data and return values grouped by filterfield
const convertLineGraphFilter = (data, valueField, filterField, fields, multiplyBy) => {
  const allYears = data.map((a) => a.Year);
  const distinct_years = [...new Set(allYears)].sort();
  const values = [];

  for (let j = 0; j < fields.length; j++) {
    values.push([]);
  }

  distinct_years.forEach((year) => {
    const rows = data.filter((d) => d.Year === year);
    fields.forEach((field, index) => {
      rows.forEach((row) => {
        if (row[filterField] === field) {
          values[index].push(multiplyBy > 0 ? row[valueField] * multiplyBy : row[valueField]);
        }
      });
    });
  });

  return {
    // years
    years: distinct_years,
    // data
    values,
  };
};

const returnSingleValue = (data, field, divideBy, multiplyBy) => {
  // If no data is provided, return default value and indicate no data
  if (!data) {
    return {
      value: 0,
      nodata: true,
    };
  }

  let value;

  // Check if field is an array
  if (Array.isArray(field)) {
    // Map the field array to get corresponding values from data
    const values = field.map((f) => data[f]).filter((v) => v !== undefined && v !== null);
    // If no valid values are found, return default value and indicate no data
    if (values.length === 0) {
      return {
        value: 0,
        nodata: true,
      };
    }
    // Get the minimum value among the fields
    value = Math.min(...values);
  } else {
    // If field is a string, get the value directly from data
    value = data[field];
    // If the value is undefined, return default value and indicate no data
    if (value === undefined || value === null) {
      return {
        value: 0,
        nodata: true,
      };
    }
  }

  // If divideBy is provided and value is not zero, divide the value
  if (divideBy && value !== 0.0) {
    value /= divideBy;
  } else if (multiplyBy) {
    value *= multiplyBy;
  }

  // Return the calculated value and indicate data was found
  return {
    value,
    nodata: false,
  };
};

export {
  calculateColourValuesThree,
  calculateColourValues,
  convertAreaLineGraph,
  convertLineGraph,
  convertLineGraphFilter,
  returnSingleValue,
};
